import React, { PureComponent } from 'react'
import PropTypes from 'prop-types'
import { Map as LeafletMap, TileLayer, Polygon, Polyline, Marker, Tooltip, CircleMarker } from 'react-leaflet'
import { divIcon } from 'leaflet'
import L from 'leaflet'
import { createLocation, getBoundingBox, getLatitude, getLongitude, insidePolygon } from 'geolocation-utils'
import { cloneDeep, uniqBy, isUndefined } from 'lodash'
import moment from 'moment'

// Components
import SpinnerOverlay from '../spinnerOverlay/SpinnerOverlay'
// CSS
import 'leaflet/dist/leaflet.css'
// JS
import { MAP_URL, simpleFormatTimeString, startDateFormatString } from '../../js/constants'
import { getAllBerthsAtQuays } from '../../utils/general'
import { latLongFromBearingAndDistance } from '../../utils/geoUtils'
import { getBounds } from '../../utils/geoUtils'
import { handleFetchError } from '../../js/sentry'

export default class ShipsMap extends PureComponent {
  static propTypes = {
    config: PropTypes.object.isRequired,
    selectedShip: PropTypes.string,
    onSelectShip: PropTypes.func,
    getShipsDetailsByMmsiList: PropTypes.func.isRequired,
    getShipDetailsInTheRadius: PropTypes.func.isRequired,
    terminalVisits: PropTypes.array.isRequired,
    zoomToFitShips: PropTypes.bool.isRequired,
    showShipPopupWithEta: PropTypes.bool.isRequired,
    filters: PropTypes.array,
    hoveredShip: PropTypes.string
  }

  getAllShipsInTerminalIntervalID

  constructor(props) {
    super(props)

    //  To convert string to boolean as storage objects can only store strings
    const showUnPlannedShips = window.localStorage.getItem('showUnPlannedShips')
      ? JSON.parse(window.localStorage.getItem('showUnPlannedShips'))
      : false

    this.state = {
      loadingShips: false,
      plannedShips: [],
      allShipsInTerminal: [],
      zoomToFitShips: props.zoomToFitShips,
      showUnPlannedShips: showUnPlannedShips
    }
  }

  componentDidMount() {
    if (this.props.terminalVisits.length && !this.state.plannedShips.length) {
      this.getShipInformationFromTerminalVisits(this.props.terminalVisits)
    }
    if (this.props.config.center) {
      this.getAllShipsInTerminal(this.props.config.center)
      // Start interval after calling it the first time
      this.getAllShipsInTerminalIntervalID = setInterval(
        () => this.getAllShipsInTerminal(this.props.config.center),
        60000
      )
    }
  }

  componentDidUpdate(prevProps) {
    if (!prevProps.terminalVisits.length && this.props.terminalVisits.length && !this.state.plannedShips.length) {
      this.getShipInformationFromTerminalVisits(this.props.terminalVisits)
    }
  }
  componentWillUnmount() {
    clearTimeout(this.getAllShipsInTerminalIntervalID)
  }

  render() {
    let bounds = this.state.zoomToFitShips
      ? this.calculateShipsMapBounds(this.state.plannedShips)
      : this.getDefaultInitialMapBounds()

    if (this.props.selectedShip) {
      const ship = this.state.plannedShips.find(ship => ship.mmsi === this.props.selectedShip)
      bounds = this.calculateSelectedShipMapBounds(ship, this.props.config.center)
    }

    return (
      <div className="map-container">
        <SpinnerOverlay loading={this.state.loadingShips} />
        <LeafletMap bounds={bounds}>
          <TileLayer url={MAP_URL} tileSize={512} zoomOffset={-1} />
          {this.renderTerminalOnMap(this.props.config)}
          {this.renderBerthsOnMap(this.props.config)}
          {this.renderShipsOnMap(this.state.plannedShips)}
          {this.state.showUnPlannedShips && this.renderUnPlannedShips(this.state.plannedShips)}
          <div className="leaflet-bottom leaflet-left">
            <div className="leaflet-control leaflet-bar">
              <button onClick={this.toggleUnPlannedShips} title="Toggle unplanned visits">
                <i className="fa fa-ship" />
              </button>
              <button onClick={() => this.setState({ zoomToFitShips: true })} title="Zoom out to view all ships">
                <i className="fa fa-globe-europe" />
              </button>
              <button onClick={() => this.setState({ zoomToFitShips: false })} title="Zoom in on terminal">
                <i className="fas fa-warehouse" />
              </button>
            </div>
          </div>
        </LeafletMap>
      </div>
    )
  }

  getAllShipsInTerminal = async center => {
    const boundingRadius = 800 //metres
    this.setState({ loadingShips: true })

    // Find all the ships in the bounding radius of the terminal
    try {
      const fetchedShips = await this.props.getShipDetailsInTheRadius(center, boundingRadius)
      this.setState({ allShipsInTerminal: fetchedShips })
    } catch (err) {
      handleFetchError(err)
    } finally {
      this.setState({ loadingShips: false })
    }
  }

  getShipInformationFromTerminalVisits = async terminalVisits => {
    if (!terminalVisits.length) {
      this.setState({ plannedShips: [] })
      return
    }

    this.setState({ loadingShips: true })

    // Get all the mmsis that are in the terminal visits and make a unique array
    const mmsiList = uniqBy(
      terminalVisits.filter(tV => tV.ship && tV.ship.mmsi),
      'ship.mmsi'
    ).map(tV => tV.ship.mmsi)

    // Use the mmsi list to get all the ships and put them in the state
    try {
      const fetchedShips = await this.props.getShipsDetailsByMmsiList(mmsiList)
      this.setState({ plannedShips: fetchedShips })
    } catch (err) {
      handleFetchError(err)
    } finally {
      this.setState({ loadingShips: false })
    }
  }

  calculateShipsMapBounds = ships => {
    if (!ships.length) {
      return this.getDefaultInitialMapBounds()
    }

    // create locations in lat lng format
    const locations = ships
      .filter(ship => !isUndefined(ship.location))
      .map(ship => createLocation(ship.location.latitude, ship.location.longitude, 'LatLon'))

    locations.push(createLocation(this.props.config.center[0], this.props.config.center[1], 'LatLon'))

    const boundingBox = getBoundingBox(locations, undefined)

    //return the initialMapBounds
    return L.latLngBounds(
      // @ts-ignore
      L.latLng(getLatitude(boundingBox.topLeft), getLongitude(boundingBox.topLeft)),
      // @ts-ignore
      L.latLng(getLatitude(boundingBox.bottomRight), getLongitude(boundingBox.bottomRight))
    )
  }

  getDefaultInitialMapBounds = () => {
    const lats = this.props.config.boundingBox.map(bb => bb[0])
    const lngs = this.props.config.boundingBox.map(bb => bb[1])

    // Set the initial mapbounds (bounding box around terminal)
    const zoomOutPadding = 0.006
    return L.latLngBounds(
      L.latLng(Math.max(...lats) + zoomOutPadding, Math.max(...lngs) + zoomOutPadding),
      L.latLng(Math.min(...lats) - zoomOutPadding, Math.min(...lngs) - zoomOutPadding)
    )
  }

  /**
   * Render the terminal on the map
   * @param terminal
   */
  renderTerminalOnMap = terminal => {
    const emptyIcon = divIcon({
      html: '<div/>',
      iconAnchor: [0, 0]
    })

    return (
      <Marker position={terminal.center} opacity={0} icon={emptyIcon}>
        <Tooltip permanent={true}>
          <span>{terminal.name}</span>
        </Tooltip>
      </Marker>
    )
  }

  /**
   * Render the berths on the map
   * @param config
   */
  renderBerthsOnMap = config => {
    return getAllBerthsAtQuays(config.quays)
      .filter(berth => berth?.berth?.area?.coordinates?.[0])
      .map(berth => {
        return (
          <Polygon
            key={berth.berthId}
            weight={5}
            positions={berth.berth.area.coordinates[0].map(c => [c[1], c[0]])}
            opacity={1}
          >
            <Tooltip>
              <span>{berth.terminalName}</span>
            </Tooltip>
          </Polygon>
        )
      })
  }

  /**
   * Render the selected ship on the map
   * @param ships
   */
  renderShipsOnMap = ships => {
    const filteredShips = ships
      .filter(ship => (this.props.terminalVisits || []).find(visit => visit.ship.mmsi === ship.mmsi))
      .filter(ship => !isUndefined(ship.location))

    if (!filteredShips.length) {
      return null
    }

    return filteredShips.map(ship => {
      const trueHeading = ship.trueHeading !== 511 ? ship.trueHeading : ship.courseOverGround
      const latLongBow = latLongFromBearingAndDistance(
        ship.location,
        ship.positionOfTransponder ? ship.positionOfTransponder.distanceToBow : 50,
        trueHeading
      )
      const latLongStern = latLongFromBearingAndDistance(
        ship.location,
        ship.positionOfTransponder ? -ship.positionOfTransponder.distanceToStern : -50,
        trueHeading
      )

      const lineString = [latLongBow, latLongStern]

      const showToolTip =
        this.props.showShipPopupWithEta || this.props.selectedShip === ship.mmsi || this.props.hoveredShip === ship.mmsi
          ? true
          : false
      const shipTimeFormatted = this.props.showShipPopupWithEta ? this.getShipTimeFormatted(ship) : null
      const shipMainColor =
        ship.timeLastUpdate < moment().subtract(1, 'days').valueOf()
          ? '#81a0cd' //$color-grey
          : '#2f61ff'
      const shipColor = showToolTip ? 'red' : shipMainColor

      const updatedTime = moment(ship.timeLastUpdate).format(startDateFormatString)

      if (showToolTip) {
        return (
          // @ts-ignore TODO: fix type checking
          <ExtendedPolyline
            positions={lineString}
            weight={15}
            color={shipColor}
            opacity={1}
            key={ship.mmsi}
            onClick={() => this.onSelectShip(ship.mmsi)}
          >
            <Tooltip className="ship-tooltip" permanent={this.props.showShipPopupWithEta}>
              <div>
                <span>
                  {ship.name} {shipTimeFormatted ? `(${shipTimeFormatted})` : null}
                </span>
                <br />
                <span>Updated: {updatedTime}</span>
              </div>
            </Tooltip>
          </ExtendedPolyline>
        )
      } else {
        return (
          <Polyline
            positions={lineString}
            weight={15}
            color={shipColor}
            opacity={1}
            key={ship.mmsi}
            onClick={() => {
              this.onSelectShip(ship.mmsi)
            }}
          >
            <Tooltip className="ship-tooltip">
              <div>
                <span>
                  {ship.name}
                  <br />
                  Updated: {updatedTime}
                </span>
              </div>
            </Tooltip>
          </Polyline>
        )
      }
    })
  }

  renderUnPlannedShips = plannedShips => {
    if (!this.state.allShipsInTerminal.length) {
      return null
    }

    const filteredPlannedShips = plannedShips
      .filter(ship => (this.props.terminalVisits || []).find(visit => visit.ship.mmsi === ship.mmsi))
      .filter(ship => !isUndefined(ship.location))

    const unPlannedShips = this.state.allShipsInTerminal.filter(
      ship => !filteredPlannedShips.find(s => ship.mmsi === s.mmsi)
    )

    return unPlannedShips.map(ship => {
      const trueHeading = ship.trueHeading !== 511 ? ship.trueHeading : ship.courseOverGround
      const latLongBow = latLongFromBearingAndDistance(
        ship.location,
        ship.positionOfTransponder ? ship.positionOfTransponder.distanceToBow : 50,
        trueHeading
      )
      const latLongStern = latLongFromBearingAndDistance(
        ship.location,
        ship.positionOfTransponder ? -ship.positionOfTransponder.distanceToStern : -50,
        trueHeading
      )

      const lineString = [latLongBow, latLongStern]
      const terminalBoundingBox = this.props.config.boundingBox.map(coordinates => [...coordinates].reverse()) // Because they are inversed
      const isShipInsideTerminalBoundingBox = this.checkShipInside(ship, terminalBoundingBox)
      const shipColor = isShipInsideTerminalBoundingBox ? '#ff9800' : '#c0c0c0'

      const updatedTime = moment(ship.timeLastUpdate).format(startDateFormatString)

      return ship.trueHeading !== 511 ? (
        <Polyline
          positions={lineString}
          weight={15}
          color={shipColor}
          opacity={1}
          key={ship.mmsi}
          onClick={() => {
            this.onSelectShip(ship.mmsi)
          }}
        >
          <Tooltip className="ship-tooltip">
            <div>
              <span>
                {ship.name}
                <br />
                Updated: {updatedTime}
              </span>
            </div>
          </Tooltip>
        </Polyline>
      ) : (
        <CircleMarker
          center={cloneDeep(ship.location.coordinates).reverse()} // reverse the coordinates to be [Lat, Lon]
          color={shipColor}
          opacity={1}
          key={ship.mmsi}
          fillColor={shipColor}
          fill
          radius={5}
          fillOpacity={1}
          onClick={() => {
            this.onSelectShip(ship.mmsi)
          }}
        >
          <Tooltip className="ship-tooltip">
            <div>
              <span>
                {ship.name}
                <br />
                Updated: {updatedTime}
              </span>
            </div>
          </Tooltip>
        </CircleMarker>
      )
    })
  }

  getShipTimeFormatted = ship => {
    const shipAlreadyLeft = ship.atd ? true : false
    if (shipAlreadyLeft) {
      return null
    }

    const shipAlreadyAtTerminal = ship.ata ? true : false
    const etaOrEtd = shipAlreadyAtTerminal ? 'ETD' : 'ETA'
    const timeStamp = etaOrEtd === 'ETA' ? ship.eta : ship.etd
    const isToday = timeStamp && moment(timeStamp).isSame(moment(), 'day')
    const isTomorrow = timeStamp && moment(timeStamp).isSame(moment().add(1, 'day'), 'day')
    if (!timeStamp || (!isToday && !isTomorrow)) {
      return null
    }

    return etaOrEtd + ' ' + (isTomorrow ? 'tomorrow ' : '') + moment(ship.eta).format(simpleFormatTimeString)
  }

  onSelectShip = mmsi => {
    if (this.props.onSelectShip) {
      this.props.onSelectShip(mmsi)
    }
  }

  calculateSelectedShipMapBounds = (selectedShip, terminalCenter) => {
    const shipLocation =
      selectedShip && selectedShip.location ? cloneDeep(selectedShip.location.coordinates).reverse() : null

    // Initialize the mapbounds
    return selectedShip && shipLocation ? getBounds([terminalCenter, shipLocation]) : this.getDefaultInitialMapBounds()
  }

  toggleUnPlannedShips = () => {
    const showShips = !this.state.showUnPlannedShips
    this.setState({ showUnPlannedShips: showShips })
    window.localStorage.setItem('showUnPlannedShips', `${showShips}`)
  }

  checkShipInside = (ship, terminalBoundingBox) => {
    if (Object.prototype.hasOwnProperty.call(ship, 'location')) {
      if (this.checkCoordinatesInside(ship.location.coordinates, terminalBoundingBox)) {
        return true
      }
    }
    if (Object.prototype.hasOwnProperty.call(ship, 'locationBow')) {
      if (this.checkCoordinatesInside(ship.locationBow.coordinates, terminalBoundingBox)) {
        return true
      }
    }
    if (Object.prototype.hasOwnProperty.call(ship, 'locationStern')) {
      if (this.checkCoordinatesInside(ship.locationStern.coordinates, terminalBoundingBox)) {
        return true
      }
    }

    return false
  }

  checkCoordinatesInside = (coordinates, terminalBoundingBox) => {
    return insidePolygon(coordinates, terminalBoundingBox)
  }
}

// Create your own class, extending from the Marker class.
/**
 * @class {ExtendedPolyline}
 * @extends {Polyline}
 */
class ExtendedPolyline extends Polyline {
  // "Hijack" the component lifecycle.
  componentDidMount() {
    // Call the Marker class componentDidMount (to make sure everything behaves as normal)
    super.componentDidMount()

    // Access the marker element and open the popup.
    // @ts-ignore TODO: fix type checking
    this.leafletElement.openTooltip()
  }
}
